import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";

import Checkbox from "../components/Checkbox";
import Icon from "../components/Icon";
import Select from "../components/Select";
import TextInput from "../components/TextInput";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import {
  IS_DEV,
  LAND_SITUATION_NSW_DESKTOP_HL,
  LAND_SITUATION_NSW_DESKTOP_KDR,
  LAND_SITUATION_NSW_DESKTOP_LOOKING,
  LAND_SITUATION_NSW_DESKTOP_VACANT,
  LAND_SITUATION_NSW_KIOSK_HAVE,
  LAND_SITUATION_NSW_KIOSK_LOOKING,
  LEADS_STATUS_NSW_BROWSING,
  LEADS_STATUS_NSW_NEW,
  LEADS_STATUS_NSW_OLD,
} from "../resources/constants";
import { scrollToTop } from "../util";

const NswFinalDetailsScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    scrollToTop();
    navContext.setOfferTickerVisible(false);
    navContext.setFooterModePrimary(); // In case the user presses browser-back
    navContext.setNextButtonEnabled(false);
    navContext.setNextBtnOnClick(() => {
      if(userContext.isKioskMode){
        if(!IS_DEV){
          try {
            // @ts-ignore
            window.gtag("event", "SUCCEEDED", {
              "event_category": "ENQUIRY", 
              "event_label": "pyht-sign-up"
            });
            // @ts-ignore
            window.ttq.track("SubmitForm", {
              "contents": [
                {
                  "content_id": window.location.href,
                  "content_type": "Form Submitted Successfully",
                  "content_name": "pyht-final-details"
                }
              ]
            });
            // @ts-ignore
            window.fbq("track", "SubmitApplication");
            // @ts-ignore
            uet_report_conversion();
          } catch (e) {
            console.error(e);
          }
        }
        navContext.goNext();
      }else{
        submitForm();
      }
    });
  }, []);

  useEffect(() => {
    if (apiContext.isLoading) {
      navContext.setNextButtonEnabled(false);
      return;
    }
    if(userContext.isKioskMode){
      if (
        userContext.firstName !== null && userContext.firstName.trim() !== "" &&
        userContext.lastName !== null && userContext.lastName.trim() !== "" &&
        userContext.email !== null && userContext.email.trim() !== "" &&
        /\S+@\S+\.\S+/.test(userContext.email) && // Email regex
        userContext.phone !== null && userContext.phone !== ""
      ) {
        navContext.setNextButtonEnabled(true);
      } else {
        navContext.setNextButtonEnabled(false);
      }
    }else{
      if (
        userContext.firstName !== null && userContext.firstName.trim() !== "" &&
        userContext.lastName !== null && userContext.lastName.trim() !== "" &&
        userContext.email !== null && userContext.email.trim() !== "" &&
        /\S+@\S+\.\S+/.test(userContext.email) && // Email regex
        userContext.phone !== null && userContext.phone !== ""
      ) {
        navContext.setNextButtonEnabled(true);
      } else {
        navContext.setNextButtonEnabled(false);
      }
    }
  }, [
    userContext.firstName,
    userContext.lastName,
    userContext.email,
    userContext.consultant,
    userContext.phone,
    apiContext.isLoading,
  ]);

  const submitForm = async () => {
    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    setPhoneError("");
    try {
      const result = await apiContext.emailSummary({
        name: userContext.firstName,
        surname: userContext.lastName,
        email: userContext.email,
        phone: userContext.phone,
        remember: userContext.rememberDetails,
        opt_in: false,
        region: apiContext.region,
        email_secondary: userContext.emailSecondary,
        land_situation: userContext.landSituation,
        lead_status: userContext.leadStatus,
        consultant: userContext.consultant,
      });

      if (result.errors) {
        if (result.errors.name) {
          setFirstNameError(result.errors.name[0]);
        }
        if (result.errors.surname) {
          setLastNameError(result.errors.surname[0]);
        }
        if (result.errors.email) {
          setEmailError(result.errors.email[0]);
        }
        if (result.errors.phone) {
          setPhoneError(result.errors.phone[0]);
        }
      } else if (result.error) {
        alert(result.error);
      } else {
        if(!IS_DEV){
          try {
            // @ts-ignore
            window.gtag("event", "SUCCEEDED", {
              "event_category": "ENQUIRY", 
              "event_label": "pyht-sign-up"
            });
            // @ts-ignore
            window.ttq.track("SubmitForm", {
              "contents": [
                {
                  "content_id": window.location.href,
                  "content_type": "Form Submitted Successfully",
                  "content_name": "pyht-final-details"
                }
              ]
            });
            // @ts-ignore
            window.fbq("track", "SubmitApplication");
            // @ts-ignore
            uet_report_conversion();
          } catch (e) {
            console.error(e);
          }
        }
        navContext.goNext();
      }
    } catch (e: any) {
      alert(e.message);
    }
  };

  const getConsultantOptions = () => {
    const options = [{ key: "", value: "Please select your new home consultant" }];
    for (const consultant of apiContext.consultantResults) {
      options.push({
        key: consultant.id,
        value: consultant.name,
      });
    }
    return options;
  };

  const getRegionOptions = () => {
    const options = [{ key: "", value: "Please select your preferred build area" }];
    for (const region of apiContext.nswRegionResults) {
      options.push({
        key: region.id,
        value: region.title,
      });
    }
    return options;
  };

  const getLeadStatusOptions = () => {
    const options = [
      { key: "", value: "Please select" },
      { key: LEADS_STATUS_NSW_NEW, value: LEADS_STATUS_NSW_NEW },
      { key: LEADS_STATUS_NSW_OLD, value: LEADS_STATUS_NSW_OLD },
      { key: LEADS_STATUS_NSW_BROWSING, value: LEADS_STATUS_NSW_BROWSING },
    ];
    return options;
  };

  return (
    <div className={"screen screen-final-details screen-nsw-final-details container"}>
      {userContext.isKioskMode ? (
        <>
          <h2 className="h1">Get a copy of your new home selections!</h2>
          <p className={"subtitle"}>
            To receive an email copy of your selections enter the details below: 
          </p>
        </>
      ) : (
        <>
          <h2 className="h1">Receive a summary of your new home!</h2>
          <p className={"subtitle"}>
            To view your detailed estimate and receive an email copy of your selections enter the details below: 
          </p>
        </>
      )}

      <form className={"final-details-form"}>
        <div className={"form-group fname"}>
          <p className={"label"}>First Name</p>
          <div className={"form-controls"}>
            <TextInput
              placeholder={"First Name*"}
              value={userContext.firstName}
              onChange={(value) => userContext.setFirstName(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              disabled={apiContext.isLoading}
            />
            {firstNameError !== "" && (
              <p className={"error-msg"}>{firstNameError}</p>
            )}
          </div>
        </div>
        <div className={"form-group lname"}>
          <p className={"label"}>Last Name</p>
          <div className={"form-controls"}>
            <TextInput
              placeholder={"Last Name*"}
              value={userContext.lastName}
              onChange={(value) => userContext.setLastName(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              disabled={apiContext.isLoading}
            />
            {lastNameError !== "" && (
              <p className={"error-msg"}>{lastNameError}</p>
            )}
          </div>
        </div>
        <div className={"form-group email"}>
          <p className={"label"}>Primary Email</p>
          <div className={"form-controls"}>
            <TextInput
              placeholder={"Email*"}
              value={userContext.email}
              onChange={(value) => userContext.setEmail(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              disabled={apiContext.isLoading}
            />
            {emailError !== "" && (
              <p className={"error-msg"}>{emailError}</p>
            )}
          </div>
        </div>
        {userContext.isKioskMode && (
          <div className={"form-group email"}>
            <p className={"label"}>Secondary Email</p>
            <div className={"form-controls"}>
              <TextInput
                placeholder={"Email"}
                value={userContext.emailSecondary}
                onChange={(value) => userContext.setEmailSecondary(value.trim())}
                onScreenKeyboard={userContext.isKioskMode}
                disabled={apiContext.isLoading}
              />
            </div>
          </div>
        )}
        <div className={"form-group phone"}>
          <p className={"label"}>Mobile Phone</p>
          <div className={"form-controls"}>
            <TextInput
              placeholder={"Mobile Phone*"}
              value={userContext.phone}
              onChange={(value) => userContext.setPhone(value.trim())}
              onScreenKeyboard={userContext.isKioskMode}
              disabled={apiContext.isLoading}
            />
            {phoneError !== "" && (
              <p className={"error-msg"}>{phoneError}</p>
            )}
          </div>
        </div>
        <div className={"form-group"}>
          <p className={"label"}>Preferred Build Area</p>
          <div className={"form-controls"}>
            <Select
              id={"select-region"}
              options={getRegionOptions()}
              value={apiContext.region ? apiContext.region.id.toString() : ""}
              onChange={(regionId) => apiContext.setRegion(apiContext.nswRegionsKeyedById[regionId])}
            />
          </div>
        </div>
        {userContext.isKioskMode ? (
          <>
            <div className={"form-group"}>
              <p className={"label"}>Consultant</p>
              <div className={"form-controls"}>
                <Select
                  id={"select-consultant"}
                  options={getConsultantOptions()}
                  value={userContext.consultant ? userContext.consultant.id.toString() : ""}
                  onChange={(consultantId) => userContext.setConsultant(apiContext.consultantsKeyedById[consultantId])}
                />
              </div>
            </div>
            <div className={"form-group land-situation"}>
              <p className={"label"}>Land Situation</p>
              <div className={"form-controls"}>
                {[
                  LAND_SITUATION_NSW_KIOSK_LOOKING,
                  LAND_SITUATION_NSW_KIOSK_HAVE,
                ].map((item) => {
                  return (
                    <button
                      type="button"
                      key={item}
                      className={`btn ${
                        userContext.landSituation !== item
                          ? "btn-secondary"
                          : ""
                      }`}
                      onClick={() => userContext.setLandSituation(item)}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={"form-group land-situation"}>
              <p className={"label"}>What best describes your situation?</p>
              <div className={"form-controls land-situation-options"}>
                {[
                  LAND_SITUATION_NSW_DESKTOP_VACANT,
                  LAND_SITUATION_NSW_DESKTOP_LOOKING,
                  LAND_SITUATION_NSW_DESKTOP_KDR,
                  LAND_SITUATION_NSW_DESKTOP_HL,
                ].map((item) => {
                  return (
                    <div key={item} className="land-situation-option">
                      <button
                        type="button"
                        className={`btn ${
                          userContext.landSituation !== item
                            ? "btn-secondary"
                            : ""
                        }`}
                        onClick={() => userContext.setLandSituation(item)}
                      >
                        <Icon name={item} />
                        <span>{item}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={"form-group"}>
              <p className={"label"}>Would you like a sales consultant to make contact with you?</p>
              <div className={"form-controls"}>
                <Select
                  id={"select-lead-status"}
                  options={getLeadStatusOptions()}
                  value={userContext.leadStatus ? userContext.leadStatus : ""}
                  onChange={(leadStatus) => userContext.setLeadStatus(leadStatus)}
                />
              </div>
            </div>
            {userContext.leadStatus === LEADS_STATUS_NSW_OLD && (
              <div className={"form-group"}>
                <p className={"label"}>Consultant</p>
                <div className={"form-controls"}>
                  <Select
                    id={"select-consultant"}
                    options={getConsultantOptions()}
                    value={userContext.consultant ? userContext.consultant.id.toString() : ""}
                    onChange={(consultantId) => userContext.setConsultant(apiContext.consultantsKeyedById[consultantId])}
                  />
                </div>
              </div>
            )}
            <div className={"form-group form-remember"}>
              <p className={"label"}>&nbsp;</p>
              <div className={"form-controls"}>
                <Checkbox
                  checked={userContext.rememberDetails}
                  onChange={(value) => userContext.setRememberDetails(value)}
                  disabled={apiContext.isLoading}
                >
                  <>
                Remember my details for next time
                  </>
                </Checkbox>
              </div>
            </div>
          </>
        )}
        <div className={"form-group form-remember"}>
          <p className={"label"}>&nbsp;</p>
          <div className={"form-controls"}>
            <Checkbox
              checked={userContext.interestedInGrannyFlats}
              onChange={(value) => userContext.setInterestedInGrannyFlats(value)}
              disabled={apiContext.isLoading}
            >
              <>
                {"I'm interested in granny flats"}
              </>
            </Checkbox>
          </div>
        </div>
        <div className={"form-group form-remember"}>
          <p className={"label"}>&nbsp;</p>
          <div className={"form-controls"}>
            <Checkbox
              checked={userContext.sendToFinanceManager}
              onChange={(value) => userContext.setSendToFinanceManager(value)}
              disabled={apiContext.isLoading}
            >
              <>
                I would like a free finance consultation
              </>
            </Checkbox>
          </div>
        </div>
        <div className={"form-group form-disclaimer"} dangerouslySetInnerHTML={{ __html: apiContext.finalDetailsSettings.form_disclaimer }} />
      </form>
    </div>
  );
});

export default NswFinalDetailsScreen;
